import { useState, useEffect, useMemo, useCallback } from 'react';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';
import {
  useDocumentCandidates,
  DocumentCandidate,
  DocumentCandidateType,
  DocumentCandidateStatus,
  DocumentReviewAction,
} from '@/hooks/useDocumentCandidates';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  CheckCircle2,
  XCircle,
  Edit3,
  FileText,
  Clock,
  Loader2,
  FolderKanban,
  Sparkles,
  Briefcase,
  Target,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig: Record<DocumentCandidateStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  suggested: { label: 'Suggested', variant: 'default' },
  approved: { label: 'Approved', variant: 'default' },
  rejected: { label: 'Rejected', variant: 'destructive' },
  modified: { label: 'Modified', variant: 'secondary' },
};

const typeLabel: Record<DocumentCandidateType, string> = {
  project: 'Project',
  portfolio: 'Portfolio',
  strategy: 'Strategy',
  budget: 'Budget',
};

const typeIcon: Record<DocumentCandidateType, typeof FolderKanban> = {
  project: FolderKanban,
  portfolio: Briefcase,
  strategy: Target,
  budget: Wallet,
};

// Kanban columns are the four candidate types, in the order they route
// to (or, for 'strategy', will route to once a destination table exists).
const COLUMNS: DocumentCandidateType[] = ['project', 'portfolio', 'budget', 'strategy'];

// Sentinel for "create as new project" in the picker — distinct from any
// real project id, and distinct from leaving the field untouched.
const CREATE_NEW = '__create_new__';

interface ProjectOption {
  id: string;
  name: string;
}

const DocumentIntelligencePanel = () => {
  const { role } = useUserRole();
  const { items, loading, submitting, submitReview } = useDocumentCandidates();

  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [statusFilter, setStatusFilter] = useState<DocumentCandidateStatus | 'all'>('suggested');

  // Review dialog state
  const [selected, setSelected] = useState<DocumentCandidate | null>(null);
  const [action, setAction] = useState<DocumentReviewAction | null>(null);
  const [candidateType, setCandidateType] = useState<DocumentCandidateType>('project');
  const [notes, setNotes] = useState('');
  const [editName, setEditName] = useState('');
  const [editBudget, setEditBudget] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editPortfolio, setEditPortfolio] = useState('');
  const [linkProjectId, setLinkProjectId] = useState<string>(CREATE_NEW);
  const [linkProjectIds, setLinkProjectIds] = useState<string[]>([]);

  const canReview = [
    'project_manager',
    'senior_project_manager',
    'program_manager',
    'pmo',
    'executive',
    'admin',
  ].includes(role || '');

  // Single flat fetch (id, name only — not projects_sync's full row,
  // which carries raw_data/team_data/milestones_data JSONB) that feeds
  // both the project-picker dropdown and the "Suggested match" name
  // lookup, avoiding N+1 per-card queries.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('projects_sync')
        .select('id, name')
        .order('name');
      if (!cancelled) {
        if (error) {
          console.error('Failed to load projects for document review:', error);
        } else {
          setProjects((data as ProjectOption[]) || []);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const projectNameById = useMemo(() => {
    const map = new Map<string, string>();
    projects.forEach((p) => map.set(p.id, p.name));
    return map;
  }, [projects]);

  const openAction = useCallback((item: DocumentCandidate, a: DocumentReviewAction) => {
    setSelected(item);
    setAction(a);
    setCandidateType(item.candidate_type);
    setNotes('');
    setEditName(item.name);
    setEditBudget(item.detected_budget != null ? String(item.detected_budget) : '');
    setEditStartDate(item.detected_start_date || '');
    setEditEndDate(item.detected_end_date || '');
    setEditPortfolio(item.detected_portfolio || '');
    setLinkProjectId(item.matched_project_id || CREATE_NEW);
    setLinkProjectIds(item.matched_project_id ? [item.matched_project_id] : []);
  }, []);

  const closeAction = () => {
    setSelected(null);
    setAction(null);
  };

  const requiresSingleProjectLink = candidateType === 'budget';
  const requiresMultiProjectLink = candidateType === 'portfolio';
  const showsProjectLinkPicker = candidateType === 'project' || requiresSingleProjectLink;

  const blockedReason =
    action !== 'reject' && requiresSingleProjectLink && (linkProjectId === CREATE_NEW || !linkProjectId)
      ? 'A budget candidate must be linked to an existing project.'
      : action !== 'reject' && requiresMultiProjectLink && linkProjectIds.length === 0
        ? 'A portfolio candidate must be linked to at least one project.'
        : null;

  const handleSubmit = async () => {
    if (!selected || !action) return;

    const result = await submitReview({
      id: selected.id,
      candidateType,
      action,
      notes,
      edits:
        action === 'modify'
          ? {
              name: editName,
              detected_budget: editBudget.trim() === '' ? null : Number(editBudget),
              detected_start_date: editStartDate || null,
              detected_end_date: editEndDate || null,
              detected_portfolio: editPortfolio.trim() || null,
            }
          : undefined,
      // Only project/budget-type candidates surface the single-project
      // picker (reject never touches matched_project_id).
      ...(action !== 'reject' && showsProjectLinkPicker
        ? { linkToProject: linkProjectId === CREATE_NEW ? null : linkProjectId }
        : {}),
      ...(action !== 'reject' && requiresMultiProjectLink
        ? { linkToProjects: linkProjectIds }
        : {}),
    });

    if (result.ok === false) {
      toast.error(result.error);
      return;
    }

    if (result.ingestError) {
      toast.warning(`Review saved, but ingestion failed: ${result.ingestError}`);
    } else {
      toast.success(
        action === 'reject'
          ? 'Candidate rejected'
          : result.ingested
            ? 'Candidate approved and routed'
            : 'Candidate approved',
      );
    }
    closeAction();
  };

  const counts = {
    suggested: items.filter((i) => i.status === 'suggested').length,
    approved: items.filter((i) => i.status === 'approved').length,
    rejected: items.filter((i) => i.status === 'rejected').length,
  };

  const columnItems = useMemo(() => {
    const byType: Record<DocumentCandidateType, DocumentCandidate[]> = {
      project: [], portfolio: [], strategy: [], budget: [],
    };
    for (const item of items) {
      if (statusFilter !== 'all' && item.status !== statusFilter) continue;
      byType[item.candidate_type]?.push(item);
    }
    return byType;
  }, [items, statusFilter]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          Document Intelligence
        </CardTitle>
        <CardDescription>
          Project, portfolio, strategy and budget candidates detected from synced documents —
          review before they flow into Projects, Portfolio, or Budget.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as DocumentCandidateStatus | 'all')}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="suggested">Suggested ({counts.suggested})</TabsTrigger>
            <TabsTrigger value="approved">Approved ({counts.approved})</TabsTrigger>
            <TabsTrigger value="rejected">Rejected ({counts.rejected})</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>

          <TabsContent value={statusFilter} className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No document candidates yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {COLUMNS.map((col) => {
                  const Icon = typeIcon[col];
                  const colItems = columnItems[col];
                  return (
                    <div key={col} className="flex flex-col gap-3 min-w-0">
                      <div className="flex items-center gap-2 px-1">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-semibold">{typeLabel[col]}</span>
                        <Badge variant="outline" className="ms-auto text-xs">{colItems.length}</Badge>
                      </div>

                      {colItems.length === 0 ? (
                        <div className="text-xs text-muted-foreground border border-dashed border-border rounded-lg py-6 text-center">
                          Nothing here
                        </div>
                      ) : (
                        colItems.map((item) => (
                          <div
                            key={item.id}
                            className="border border-border rounded-lg p-3 hover:bg-muted/40 transition-colors"
                          >
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <Badge variant={statusConfig[item.status]?.variant ?? 'secondary'} className="text-[10px]">
                                {statusConfig[item.status]?.label ?? item.status}
                              </Badge>
                            </div>
                            <h4 className="font-medium text-foreground text-sm">{item.name}</h4>
                            {item.description && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.description}</p>
                            )}
                            <div className="text-[11px] text-muted-foreground mt-2 flex items-center gap-2 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(item.created_at).toLocaleDateString()}
                              </span>
                              {item.detected_budget != null && (
                                <span>Budget: {item.detected_budget.toLocaleString()}</span>
                              )}
                            </div>
                            {item.matched_project_id && (
                              <div className="text-[11px] text-primary mt-1 flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                {projectNameById.get(item.matched_project_id) || 'Linked project'}
                                {item.match_confidence != null && (
                                  <span> ({Math.round(item.match_confidence * 100)}%)</span>
                                )}
                              </div>
                            )}
                            {item.review_notes && (
                              <div className="text-[11px] bg-muted/50 rounded px-2 py-1 mt-2">
                                {item.review_notes}
                              </div>
                            )}

                            {canReview && item.status === 'suggested' && (
                              <div className="flex gap-1.5 flex-wrap pt-2 mt-2 border-t border-border">
                                <Button size="sm" variant="default" className="h-7 px-2 text-xs" onClick={() => openAction(item, 'approve')}>
                                  <CheckCircle2 className="w-3.5 h-3.5 me-1" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => openAction(item, 'modify')}>
                                  <Edit3 className="w-3.5 h-3.5 me-1" /> Modify
                                </Button>
                                <Button size="sm" variant="destructive" className="h-7 px-2 text-xs" onClick={() => openAction(item, 'reject')}>
                                  <XCircle className="w-3.5 h-3.5 me-1" /> Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Review dialog: approve / modify / reject */}
      <Dialog open={!!action} onOpenChange={(o) => { if (!o) closeAction(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {action === 'approve' && 'Approve Document Candidate'}
              {action === 'reject' && 'Reject Document Candidate'}
              {action === 'modify' && 'Modify & Approve Document Candidate'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">
              <p className="font-medium">{selected?.name}</p>
              {selected?.description && <p className="text-muted-foreground">{selected.description}</p>}
            </div>

            {action !== 'reject' && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  Destination (AI suggested: {selected ? typeLabel[selected.candidate_type] : ''})
                </label>
                <Select value={candidateType} onValueChange={(v) => setCandidateType(v as DocumentCandidateType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLUMNS.map((c) => (
                      <SelectItem key={c} value={c}>{typeLabel[c]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {action === 'modify' && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Name</label>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Budget</label>
                  <Input type="number" value={editBudget} onChange={(e) => setEditBudget(e.target.value)} placeholder="Optional" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Start date</label>
                    <Input type="date" value={editStartDate ? editStartDate.slice(0, 10) : ''} onChange={(e) => setEditStartDate(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">End date</label>
                    <Input type="date" value={editEndDate ? editEndDate.slice(0, 10) : ''} onChange={(e) => setEditEndDate(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Portfolio</label>
                  <Input value={editPortfolio} onChange={(e) => setEditPortfolio(e.target.value)} placeholder="Optional" />
                </div>
              </div>
            )}

            {action !== 'reject' && showsProjectLinkPicker && (
              <div>
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <FolderKanban className="w-3.5 h-3.5" />
                  {requiresSingleProjectLink ? 'Apply budget to project (required)' : 'Link to project'}
                </label>
                <Select value={linkProjectId} onValueChange={setLinkProjectId}>
                  <SelectTrigger>
                    <SelectValue placeholder={requiresSingleProjectLink ? 'Select a project' : 'Create as new project'} />
                  </SelectTrigger>
                  <SelectContent>
                    {!requiresSingleProjectLink && (
                      <SelectItem value={CREATE_NEW}>Create as new project</SelectItem>
                    )}
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {action !== 'reject' && requiresMultiProjectLink && (
              <div>
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <Briefcase className="w-3.5 h-3.5" /> Projects in this portfolio (required, at least one)
                </label>
                <div className="max-h-40 overflow-y-auto border border-border rounded-md p-2 space-y-1.5 mt-1">
                  {projects.length === 0 && (
                    <p className="text-xs text-muted-foreground">No synced projects available yet.</p>
                  )}
                  {projects.map((p) => {
                    const checked = linkProjectIds.includes(p.id);
                    return (
                      <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            setLinkProjectIds((prev) =>
                              v ? [...prev, p.id] : prev.filter((id) => id !== p.id),
                            );
                          }}
                        />
                        {p.name}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-muted-foreground">Notes</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={action === 'reject' ? 'Reason (required)' : 'Optional notes'}
                rows={3}
              />
            </div>

            {blockedReason && (
              <p className="text-xs text-destructive">{blockedReason}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeAction}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={
                submitting ||
                (action === 'reject' && !notes.trim()) ||
                (action === 'modify' && !editName.trim()) ||
                !!blockedReason
              }
              variant={action === 'reject' ? 'destructive' : 'default'}
            >
              {submitting && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DocumentIntelligencePanel;
